import { put, takeLatest, call } from 'redux-saga/effects';
import * as actions from '../ducks/admin';
import { toastify } from '../ducks/toast';
import { request, finish } from './helpers';
import { setAdminToken, clearAdminToken } from '../utils/auth';
import { downloadCsv, date } from '../utils/format';

function* adminLogin(action) {
  const res = yield* request('POST', '/admin/login', action.payload);
  if (res.status === 'success' && res.data && res.data.token) yield call(setAdminToken, res.data.token);
  yield* finish('adminLogin', res, { toastSuccess: false });
}

function* adminLogout() {
  yield call(clearAdminToken);
}

function* load(url, payload, success) {
  const res = yield* request('POST', url, payload);
  if (res.status === 'success') yield put(success(res.data));
  else if (res.status !== 'Unauthorized') yield put(toastify({ type: 'error', msg: res.m }));
  return res;
}

function* getAffiliates(action) {
  yield* load('/admin/affiliate/list', action.payload, actions.getAffiliatesSuccess);
}

function* getAffiliateDetail(action) {
  yield* load('/admin/affiliate/detail', action.payload, actions.getAffiliateDetailSuccess);
}

function* getWithdrawals(action) {
  yield* load('/admin/affiliate/withdrawals', action.payload, actions.getWithdrawalsSuccess);
}

function* getCommissions(action) {
  yield* load('/admin/affiliate/commissions', action.payload, actions.getCommissionsSuccess);
}

//actions: run, toast, then let the page reload what it shows (flow name = action type)
function* mutate(name, url, payload) {
  const res = yield* request('POST', url, payload);
  yield* finish(name, res);
}

function* updateAffiliateStatus(action) { yield* mutate('updateAffiliateStatus', '/admin/affiliate/status', action.payload); }
function* updateWithdrawal(action) { yield* mutate('updateWithdrawal', '/admin/affiliate/withdrawal/update', action.payload); }
function* adjustCommission(action) { yield* mutate('adjustCommission', '/admin/affiliate/commission/adjust', action.payload); }
function* reverseCommission(action) { yield* mutate('reverseCommission', '/admin/affiliate/commission/reverse', action.payload); }
function* voidReferral(action) { yield* mutate('voidReferral', '/admin/affiliate/referral/void', action.payload); }

function* runScan() {
  const res = yield* request('POST', '/admin/affiliate/runScan', {});
  if (res.status === 'success' && res.data && !res.data.skipped) {
    res.m = `Scan completed: ${res.data.referrals} referrals checked, ${res.data.created} new commissions.`;
  }
  yield* finish('runScan', res);
}

//export the current filter (up to 1000 rows) as CSV
function* exportCsv(action) {
  const { type, filter } = action.payload;
  if (type === 'affiliates') {
    const res = yield* request('POST', '/admin/affiliate/list', { ...filter, page: 1, pageSize: 1000 });
    if (res.status !== 'success') return yield put(toastify({ type: 'error', msg: res.m }));
    yield call(downloadCsv, 'affiliates.csv',
      ['Email', 'Name', 'Code', 'Status', 'Verified', 'PayPal', 'Website', 'Country', 'Joined', 'Clicks', 'Sign ups', 'Paid sign ups', 'Workspaces', 'Paid workspaces', 'Earned', 'Refunded', 'Pending', 'Available', 'Paid out', 'In process', 'Balance'],
      res.data.rows.map((a) => [a.e, a.name, a.code, a.st, a.ivrfy ? 'Yes' : 'No', a.ppl, a.web, a.cn, date(a.cdt), a.clicks, a.signups, a.paidSignups, a.workspaces, a.paidWorkspaces, a.earned, a.refunded, a.pending, a.available, a.paidOut, a.inProcess, a.balance]));
  } else if (type === 'withdrawals') {
    const res = yield* request('POST', '/admin/affiliate/withdrawals', { ...filter, page: 1, pageSize: 1000 });
    if (res.status !== 'success') return yield put(toastify({ type: 'error', msg: res.m }));
    yield call(downloadCsv, 'affiliate-withdrawals.csv',
      ['Requested', 'Affiliate email', 'Affiliate name', 'Amount (USD)', 'PayPal', 'Status', 'Transaction', 'Remark', 'Updated', 'By'],
      res.data.rows.map((w) => [date(w.cdt), w.affEmail, w.affName, w.amt, w.ppl, w.st, w.txnref, w.rmk, date(w.udt), w.aby]));
  } else if (type === 'commissions') {
    const res = yield* request('POST', '/admin/affiliate/commissions', { ...filter, page: 1, pageSize: 1000 });
    if (res.status !== 'success') return yield put(toastify({ type: 'error', msg: res.m }));
    yield call(downloadCsv, 'affiliate-commissions.csv',
      ['Payment date', 'Affiliate', 'Customer', 'Workspace', 'Workspace id', 'Plan', 'Source', 'Type', 'Paid (USD)', 'Rate %', 'Months', 'Commission', 'Reversed', 'Net', 'Available from', 'Status', 'Skip reason', 'Remark', 'Source id'],
      res.data.rows.map((c) => [date(c.paydt), c.affEmail, c.customer, c.wsName, c.wsid, c.plan, c.src, c.typ, c.paid, c.rate, c.months, c.commission, c.reversed, c.net, date(c.availdt), c.status, c.skrsn, c.rmk, c.srcid]));
  }
}

export default function* adminSaga() {
  yield takeLatest(actions.adminLogin.type, adminLogin);
  yield takeLatest(actions.adminLogout.type, adminLogout);
  yield takeLatest(actions.getAffiliates.type, getAffiliates);
  yield takeLatest(actions.getAffiliateDetail.type, getAffiliateDetail);
  yield takeLatest(actions.updateAffiliateStatus.type, updateAffiliateStatus);
  yield takeLatest(actions.getWithdrawals.type, getWithdrawals);
  yield takeLatest(actions.updateWithdrawal.type, updateWithdrawal);
  yield takeLatest(actions.getCommissions.type, getCommissions);
  yield takeLatest(actions.adjustCommission.type, adjustCommission);
  yield takeLatest(actions.reverseCommission.type, reverseCommission);
  yield takeLatest(actions.voidReferral.type, voidReferral);
  yield takeLatest(actions.runScan.type, runScan);
  yield takeLatest(actions.exportCsv.type, exportCsv);
}
